import { useState } from 'react'
import { ApiError, Skeleton } from '../../../modules/core/index.js'
import { EndpointHint } from '../../../modules/core/index.js'
import { FeatureGate } from '../../../modules/add-ons/index.js'
import { useFeature } from '../../../modules/add-ons/index.js'
import type { RosterContext } from '../../../modules/core/index.js'
import { downloadSummaryCsv, useGenerateSummary, useReleaseSummary, useWorkerSummaries, useWorkerSummary } from '../hooks.js'
import { FEATURE_KEY, FEATURE_NAME } from '../labels.js'
import { GenerateSummaryForm } from './GenerateSummaryForm.js'
import { PerformanceSummaryModal } from './PerformanceSummaryModal.js'
import { SummaryList } from './SummaryList.js'
import { PerformanceHighlights } from './PerformanceHighlights.js'
import { spanLabel } from '../labels.js'
import { LoadFailure } from '../../../modules/core/index.js'

/** Inside an open roster row: generate a summary for this worker, and the ones generated before. */
function Panel({ workerId }: { workerId: string }) {
  const summaries = useWorkerSummaries(workerId)
  const generate = useGenerateSummary(workerId)
  const [isGenerating, setIsGenerating] = useState(false)
  const [openId, setOpenId] = useState<string | null>(null)
  const open = useWorkerSummary(workerId, openId)
  // The newest summary, in bold strokes above the list: the shape of the worker's year before any detail.
  const latestId = summaries.data?.[0]?.summaryId ?? null
  const latest = useWorkerSummary(workerId, latestId)
  const release = useReleaseSummary(workerId)
  const [downloading, setDownloading] = useState<string | null>(null)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  function download(summaryId: string): void {
    if (downloading !== null) {
      return
    }
    setDownloading(summaryId)
    setDownloadError(null)
    downloadSummaryCsv(workerId, summaryId)
      .catch((error: unknown) => setDownloadError(error instanceof Error ? error.message : 'The download failed.'))
      .finally(() => setDownloading(null))
  }

  function openForm(): void {
    generate.reset()
    setIsGenerating(true)
  }

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-[11px] text-ink-mid">A record of this worker's closed months for the HR system, not a performance review. Release one and the worker sees the record in their own app; your context note and your name stay with the facility.</span>
        {!isGenerating && (
          <button type="button" onClick={openForm} className="text-[11px] font-bold text-marine hover:text-marine-dk">
            Generate a summary
          </button>
        )}
      </div>
      {isGenerating && (
        <GenerateSummaryForm
          isGenerating={generate.isPending}
          error={generate.isError ? (generate.error instanceof ApiError ? generate.error.message : 'The summary could not be generated. Try again.') : null}
          onGenerate={(input) =>
            generate.mutate(input, {
              onSuccess: (summary) => {
                setIsGenerating(false)
                setOpenId(summary.summaryId)
              },
            })
          }
          onCancel={() => {
            generate.reset()
            setIsGenerating(false)
          }}
        />
      )}
      {latestId !== null && latest.data && (
        <div>
          <div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-ink-mid">Latest summary · {spanLabel(latest.data.span)}</div>
          <PerformanceHighlights summary={latest.data} />
        </div>
      )}
      {summaries.isPending ? (
        <Skeleton label="Loading summaries" />
      ) : summaries.isError ? (
        <LoadFailure size="xs" message={summaries.error instanceof ApiError ? summaries.error.message : 'Could not load this worker’s summaries.'} onRetry={() => void summaries.refetch()} retrying={summaries.isFetching} />
      ) : (
        <SummaryList
          summaries={summaries.data}
          empty="No summary has been generated for this worker yet."
          onOpen={setOpenId}
          onDownload={download}
          downloading={downloading}
          onToggleRelease={(summaryId, next) => release.mutate({ summaryId, release: next })}
          releasing={release.isPending ? (release.variables?.summaryId ?? null) : null}
        />
      )}
      {release.isError && (
        <p role="alert" className="text-[11px] text-coral">
          {release.error instanceof ApiError ? release.error.message : 'The release could not be changed. Try again.'}
        </p>
      )}
      {downloadError && (
        <p role="alert" className="text-[11px] text-coral">
          {downloadError}
        </p>
      )}
      {openId !== null && open.isError && (
        <p role="alert" className="text-[11px] text-coral">
          Could not open that summary.
        </p>
      )}
      {openId !== null && open.data && <PerformanceSummaryModal summary={open.data} onDownload={() => downloadSummaryCsv(workerId, openId)} onClose={() => setOpenId(null)} />}
    </div>
  )
}

/** The Performance tab of an open roster row: nothing for a client without the add-on, the switched-off line or the panel otherwise. */
export function RosterRowPanel({ workerId, facilityId, role, addOnsHref }: RosterContext & { workerId: string }) {
  const standing = useFeature(facilityId, FEATURE_KEY)
  if (standing.isLoading || !standing.licensed) {
    return null
  }
  return (
    <div className="bg-canvas px-5 pb-5 pt-4">
      <div className="mb-1.5 flex flex-wrap items-baseline gap-x-2 text-[9px] font-bold uppercase tracking-wide text-ink-mid">
        Performance summaries
        <EndpointHint>GET /workers/{'{workerId}'}/performance-summaries</EndpointHint>
      </div>
      <FeatureGate feature={FEATURE_KEY} name={FEATURE_NAME} facilityId={facilityId} role={role} addOnsHref={addOnsHref} compact>
        <Panel workerId={workerId} />
      </FeatureGate>
    </div>
  )
}
